import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import TicketsSidebar from '@/components/tickets/TicketsSidebar';
import TicketsFiltersBar from '@/components/tickets/TicketsFiltersBar';
import TicketsTable from '@/components/tickets/TicketsTable';
import TicketDrawer from '@/components/tickets/TicketDrawer';
import { TICKETS } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { RefreshCw, Loader2 } from 'lucide-react';

const now = new Date();
void now;

export default function TicketsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [scope, setScope] = useState('all'); // all | mine
  const [filters, setFilters] = useState({
    disposition: searchParams.get('disposition') || '',
    clinic_id: searchParams.get('clinic_id') || '',
    clinic_name: searchParams.get('clinic_name') || '',
    audiologist_id: searchParams.get('audiologist_id') || '',
    assignee_email: searchParams.get('assignee_email') || '',
    item_name: searchParams.get('item_name') || '',
    cluster: searchParams.get('cluster') || '',
    source: searchParams.get('source') || '',
    subsource: searchParams.get('subsource') || '',
    consultant_name: searchParams.get('consultant_name') || '',
    year: searchParams.get('year') || '',
    month: searchParams.get('month') || '',
    // Report drilldown params (Reports KPI cards → /tickets?disposition_group=…)
    disposition_group: searchParams.get('disposition_group') || '',
    city: searchParams.get('city') || '',
    audiologist_name: searchParams.get('audiologist_name') || '',
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || '',
    q: searchParams.get('q') || '',
    followup_today: false,
  });
  const [counts, setCounts] = useState({ total: 0, by_disposition: {} });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(searchParams.get('ticket') || null);
  const ticketParam = searchParams.get('ticket');
  useEffect(() => {
    if (ticketParam) { setSelectedId(ticketParam); }
  }, [ticketParam]);
  const scrollRef = React.useRef(null);
  const handleSelect = useCallback((id) => {
    setSelectedId(id);
    // Scroll the table region + the whole document up so the drawer header is
    // always visible even if the user clicked a row far down the list.
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, []);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;

  const [clinics, setClinics] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, e, u, s] = await Promise.all([
          api.get('/clinics'), api.get('/employees'),
          api.get('/users').catch(() => ({ data: [] })),
          api.get('/sync/status'),
        ]);
        setClinics(c.data); setEmployees(e.data); setUsers(u.data);
        setSyncStatus(s.data);
      } catch {}
    })();
  }, []);

  const paramsFor = useCallback((extra = {}) => {
    const p = { scope, ...extra };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [scope, filters]);

  const loadCounts = useCallback(async () => {
    try {
      const params = paramsFor();
      delete params.disposition; delete params.followup_today;
      const { data } = await api.get('/tickets/counts', { params });
      setCounts(data);
    } catch {}
  }, [paramsFor]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = paramsFor({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      const { data } = await api.get('/tickets', { params });
      setTickets(data);
    } catch (err) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [paramsFor, page]);

  useEffect(() => { loadCounts(); loadTickets(); }, [loadCounts, loadTickets]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/sync/run');
      if (data.status === 'success') toast.success(`Sync complete: ${JSON.stringify(data.stats)}`);
      else if (data.status === 'partial') toast.warning('Sync partial — see admin sync history');
      else toast.error(`Sync failed: ${(data.errors || []).slice(0,1).join('; ') || 'unknown'}`);
      const s = await api.get('/sync/status');
      setSyncStatus(s.data);
      loadCounts(); loadTickets();
    } catch (err) {
      toast.error('Sync failed to trigger');
    } finally {
      setSyncing(false);
    }
  };

  const lookups = useMemo(() => ({
    clinic: Object.fromEntries(clinics.map(c => [c.id, c])),
    employee: Object.fromEntries(employees.map(e => [e.id, e])),
    user: Object.fromEntries(users.map(u => [u.id, u])),
  }), [clinics, employees, users]);

  return (
    <div className="flex-1 flex overflow-hidden" data-testid={TICKETS.page}>
      <TicketsSidebar
        scope={scope} setScope={setScope}
        counts={counts}
        currentDisposition={filters.disposition}
        onSelectDisposition={(d) => { setPage(0); setFilters(f => ({ ...f, followup_today: false, disposition: d === filters.disposition ? '' : d })); }}
        followupToday={filters.followup_today}
        onToggleFollowupToday={() => { setPage(0); setFilters(f => ({ ...f, disposition: '', followup_today: !f.followup_today })); }}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white border-b border-stone-200 flex items-center px-4 py-2 gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
              {filters.followup_today ? "Today's Follow-ups" : scope === 'mine' ? 'My Patients' : 'Assigned Patients'}
            </div>
            <div className="font-heading text-lg font-bold text-stone-900">
              {filters.followup_today
                ? counts.followup_today || 0
                : filters.disposition
                ? filters.disposition.split(',').reduce((a, d) => a + (counts.by_disposition[d.trim()] || 0), 0)
                : counts.total || 0}{' '}
              <span className="text-stone-400 text-sm font-normal">patients</span>
            </div>
          </div>
          {(() => {
            const chips = [
              ['disposition_group', 'Group'], ['cluster', 'Cluster'],
              ['subsource', 'Sub-source'], ['consultant_name', 'Consultant'], ['item_name', 'Item'],
            ].filter(([k]) => filters[k]);
            if (!chips.length) return null;
            return (
              <div className="flex items-center gap-1 flex-wrap" data-testid="tickets-report-filter-chips">
                {chips.map(([k, label]) => (
                  <button key={k}
                    onClick={() => { setPage(0); setFilters(f => ({ ...f, [k]: '' })); }}
                    data-testid={`tickets-filter-chip-${k}`}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                    title="Remove filter">
                    {label}: <strong>{String(filters[k]).replace(/_/g, ' ')}</strong> ×
                  </button>
                ))}
              </div>
            );
          })()}
          <div className="flex-1" />
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
            Fundle sync:{' '}
            <span className={
              syncStatus?.fundle_enabled ? 'text-emerald-700' : 'text-stone-500'
            }>
              {syncStatus?.fundle_enabled ? 'Configured' : 'Not configured'}
            </span>
            {syncStatus?.last_run && (
              <span className="ml-1 text-stone-400">
                · last: {new Date(syncStatus.last_run.started_at).toLocaleString()} ({syncStatus.last_run.status})
              </span>
            )}
          </div>
          <Button
            onClick={runSync}
            disabled={syncing}
            size="sm"
            className="h-8 bg-stone-900 hover:bg-stone-800 text-white"
            data-testid={TICKETS.syncButton}
          >
            {syncing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Sync from Fundle
          </Button>
        </div>

        <TicketsFiltersBar
          filters={filters}
          setFilters={(fn) => { setPage(0); setFilters(fn); }}
        />

        <div ref={scrollRef} className="flex-1 overflow-auto hc-scrollbar">
          <TicketsTable
            tickets={tickets}
            loading={loading}
            lookups={lookups}
            onSelect={handleSelect}
            emptyTitle={filters.followup_today ? 'No follow-ups due today' : Object.entries(filters).some(([k, v]) => v && k !== 'followup_today') ? 'No matching patients' : undefined}
            emptyText={filters.followup_today ? 'Follow-ups set for today will appear here.' : Object.entries(filters).some(([k, v]) => v && k !== 'followup_today') ? 'No patients match these filters — try removing one.' : undefined}
          />
        </div>
        {/* Pagination */}
        <div className="bg-white border-t border-stone-200 px-4 py-2 flex items-center gap-3 text-xs">
          <span className="text-stone-600">
            Showing <strong>{tickets.length}</strong> of{' '}
            <strong>{(filters.followup_today ? counts.followup_today : filters.disposition ? filters.disposition.split(',').reduce((a, d) => a + (counts.by_disposition[d.trim()] || 0), 0) : counts.total) || 0}</strong>
          </span>
          <div className="flex-1" />
          <Button
            variant="outline" size="sm" className="h-7"
            disabled={page === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            data-testid="tickets-page-prev"
          >Prev</Button>
          <span className="text-stone-600 tabular-nums">Page {page + 1}</span>
          <Button
            variant="outline" size="sm" className="h-7"
            disabled={tickets.length < PAGE_SIZE}
            onClick={() => setPage(p => p + 1)}
            data-testid="tickets-page-next"
          >Next</Button>
        </div>
      </div>

      {selectedId && (
        <TicketDrawer
          ticketId={selectedId}
          lookups={lookups}
          users={users}
          employees={employees}
          onClose={() => setSelectedId(null)}
          onChanged={() => { loadCounts(); loadTickets(); }}
        />
      )}
    </div>
  );
}
